"use client"

import { useEffect, useRef, useState } from "react"
import gsap from "@/lib/gsap-init"
import { X, MapPin } from "lucide-react"
import logoScroll from '@/public/logoScroll.png'
import Image from "next/image"
import { Link } from "@/i18n/navigation"
import { dataService } from '@/lib/data-service'

interface SearchResult {
  id: string
  name?: string
  title?: string
  type: string
  description: string
  images?: string[]
}

interface SearchModalProps {
  isOpen: boolean
  onClose: () => void
}


export function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const resultsContainerRef = useRef<HTMLDivElement>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredResults, setFilteredResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)

  useEffect(() => {
    if (!modalRef.current) return

    if (isOpen) {
      gsap.to(modalRef.current, {
        opacity: 1,
        pointerEvents: "auto",
        duration: 0.3,
        ease: "power2.out",
      })

      searchInputRef.current?.focus()

      // Stagger animate results
      if (resultsContainerRef.current) {
        gsap.fromTo(
          resultsContainerRef.current.children,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            stagger: 0.05,
            duration: 0.4,
            ease: "back.out(1.2)",
            delay: 0.2,
          },
        )
      }
    } else {
      gsap.to(modalRef.current, {
        opacity: 0,
        pointerEvents: "none",
        duration: 0.2,
        ease: "power2.in",
      })
    }
  }, [isOpen])

  useEffect(() => {
    const performSearch = async () => {
      if (!searchTerm.trim()) {
        setFilteredResults([])
        return
      }

      setIsSearching(true)
      try {
        const results = await dataService.search(searchTerm)
        setFilteredResults(results)
      } catch (error) {
        console.error('Search failed:', error)
        setFilteredResults([])
      } finally {
        setIsSearching(false)
      }
    }

    const debounceTimer = setTimeout(performSearch, 300)
    return () => clearTimeout(debounceTimer)
  }, [searchTerm])

  const renderCard = (result: SearchResult) => {
    const getDetailLink = () => {
      const slug = result.id
      switch (result.type) {
        case 'hotels':
          return `/hotels/${slug}`
        case 'restaurants':
          return `/restaurants/${slug}`
        case 'activities':
          return `/activities/${slug}`
        case 'museums':
          return `/museums/${slug}`
        case 'destinations':
          return `/destinations/${slug}`
        default:
          return `/details/${slug}`
      }
    }

    return (
      <Link href={getDetailLink()} className="block" onClick={onClose}>
        <div className="bg-card rounded-lg p-4 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-border">
          {result.images && (
            <div className="w-full h-32 rounded-lg overflow-hidden mb-3">
              <img
                src={result.images[0]}
                alt={result.name || result.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground line-clamp-1">
                {result.name || result.title}
              </h3>
              <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full capitalize">
                {result.type.replace('s', '')}
              </span>
            </div>
            <div 
              className="text-sm text-muted-foreground line-clamp-2"
              dangerouslySetInnerHTML={{ __html: result.description }}
            />
          </div>
        </div>
      </Link>
    )
  }


  if (!isOpen) return null

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-50 flex flex-col opacity-0 pointer-events-none transition-opacity duration-300"
      style={{
        background: `linear-gradient(135deg, var(--sidebar) 0%, var(--sidebar) 100%)`,
        backdropFilter: "blur(20px)",
      }}
      onClick={onClose}
    >
      {/* Header with Logo and Close Button */}
      <div className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-sidebar-border">
        <div className="text-2xl font-bold text-sidebar-foreground">
          <Image src={logoScroll}
            height="100"
            width="160"
            alt="Carthago guide"
            className="-mt-2"
            />
        </div>
        <button
          onClick={onClose}
          className="text-sidebar-foreground hover:text-sidebar-primary transition-colors p-2 rounded-lg hover:bg-sidebar-primary/10"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Search Input */}
      <div className="flex-1 flex flex-col items-center justify-start pt-12 md:pt-20 px-6 md:px-12">
        <div className="w-full max-w-2xl">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="w-full bg-transparent text-4xl md:text-5xl text-sidebar-foreground placeholder-sidebar-foreground/40 outline-none border-b-2 border-sidebar-foreground/30 pb-6 focus:border-sidebar-primary transition-colors"
            />
          </div>

          {/* Search Results */}
          {searchTerm.trim() && (
            <div className="mt-12 max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-sidebar-primary/30 scrollbar-track-transparent">
              <div ref={resultsContainerRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pr-2">
              {isSearching ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-xl text-sidebar-foreground/50">Searching...</p>
                </div>
              ) : filteredResults.length > 0 ? (
                filteredResults.map((result) => (
                  <div key={result.id} onClick={(e) => e.stopPropagation()}>
                    {renderCard(result)}
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-xl text-sidebar-foreground/50">No results found for "{searchTerm}"</p>
                </div>
              )}
              </div>
            </div>
          )}

          {/* Popular Searches (when empty) */}
          {!searchTerm.trim() && (
            <div className="mt-12">
              <p className="text-sm text-sidebar-foreground/60 uppercase tracking-widest mb-6">Popular Searches</p>
              <div className="flex flex-wrap gap-3">
                {["Djerba", "Tunis", "Hotel", "Restaurant", "Museum"].map((term) => (
                  <button
                    key={term}
                    onClick={() => setSearchTerm(term)}
                    className="px-4 py-2 bg-sidebar-primary/10 hover:bg-sidebar-primary/20 text-sidebar-foreground rounded-full transition-colors border border-sidebar-primary/20"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
